import { type JSX, useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Group,
  Loader,
  Modal,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  type Capacidades,
  type ContatoMarcavel,
  type ListaMarcaveis,
  type PreviaDeImportacao,
  type ResultadoImportacao,
  type TagNaLista,
  api,
  explicarEnvio,
} from '../lib/api.js';
import { BarraDeFiltros, BlocoDeAjuda, Secao, Vazio } from './ui.js';
import { codigoDoCurso } from '../lib/curso.js';

/**
 * Tags: o recorte de lista que o SAGA não dá.
 *
 * O sistema acadêmico sabe tipo e curso. Não sabe "turma de janeiro",
 * "interessado do feirão" nem "acompanhante de aluno" — e é por esses recortes
 * que a secretaria manda aviso.
 *
 * AS DUAS CONTAGENS SÃO NÚMEROS DIFERENTES, e a diferença é o valor da tela.
 * Uma tag com 40 contatos e 18 alcançáveis diz que mais da metade da turma não
 * tem celular utilizável no cadastro — e é isso que manda alguém corrigir o
 * SAGA ANTES de montar o aviso, em vez de descobrir depois que 22 pessoas não
 * foram avisadas.
 */

/** Tamanho do trecho do CSV que a prévia mostra por linha. */
const COLUNAS_ESPERADAS = 'nome, telefone, tags';

function data(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR');
}

export function Tags({ capacidades }: { capacidades: Capacidades }): JSX.Element {
  const [itens, setItens] = useState<TagNaLista[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [criando, setCriando] = useState(false);
  const [editando, setEditando] = useState<TagNaLista | null>(null);
  const [marcando, setMarcando] = useState<TagNaLista | null>(null);
  const [importando, setImportando] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const { itens } = await api.get<{ itens: TagNaLista[] }>('/admin/tags');
      setItens(itens);
      setErro(null);
    } catch (e) {
      setErro(explicarEnvio(e, 'falha ao carregar as tags'));
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  return (
    <Stack gap="md">
      {erro && (
        <Alert color="red" variant="light" role="alert">
          <Group gap="sm">
            <Text size="sm">{erro}</Text>
            <Button size="xs" onClick={() => void carregar()}>
              Tentar de novo
            </Button>
          </Group>
        </Alert>
      )}

      <Secao
        numero="01"
        titulo="Tags"
        subtitulo="o recorte de lista que o sistema acadêmico não dá"
        acao={
          capacidades.disparar ? (
            <Group gap="xs">
              <Button size="xs" variant="default" onClick={() => setImportando(true)}>
                Importar CSV
              </Button>
              <Button size="xs" onClick={() => setCriando(true)}>
                Nova tag
              </Button>
            </Group>
          ) : undefined
        }
      >
        {!itens && !erro && (
          <Group gap="xs">
            <Loader size="sm" />
            <Text c="dimmed" size="sm">
              Carregando…
            </Text>
          </Group>
        )}

        {itens && itens.length === 0 && (
          <Vazio titulo="Nenhuma tag ainda.">
            Crie uma tag para o recorte que você quer avisar — “turma-2026-1”,
            “interessados-feirao” — e marque as pessoas nela. Depois é a tag que você escolhe ao
            montar um envio. Contato de fora do SAGA entra por <strong>Importar CSV</strong> ou
            pela tela de Conversas.
          </Vazio>
        )}

        {itens && itens.length > 0 && (
          <Table.ScrollContainer minWidth={640}>
            <Table highlightOnHover verticalSpacing="xs" fz="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Tag</Table.Th>
                  <Table.Th>Descrição</Table.Th>
                  <Table.Th ta="right">Contatos</Table.Th>
                  <Table.Th ta="right">Receberiam</Table.Th>
                  <Table.Th>Criada</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {itens.map((t) => {
                  const forade = t.contatos - t.alcancaveis;
                  return (
                    <Table.Tr key={t.id}>
                      <Table.Td>
                        <Text ff="monospace" size="sm">
                          {t.nome}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c={t.descricao ? undefined : 'dimmed'}>
                          {t.descricao ?? '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td ta="right">
                        <Text ff="monospace" size="sm">
                          {t.contatos}
                        </Text>
                      </Table.Td>
                      <Table.Td ta="right">
                        {/* A diferença é o recado: cor só reforça o texto ao
                            lado, nunca é o único sinal. */}
                        <Group gap={6} justify="flex-end" wrap="nowrap">
                          <Text ff="monospace" size="sm" fw={forade > 0 ? 700 : 400}>
                            {t.alcancaveis}
                          </Text>
                          {forade > 0 && (
                            <Tooltip
                              label={`${forade} sem celular utilizável no cadastro, ou que pediram para não receber`}
                              multiline
                              w={260}
                            >
                              <Badge size="xs" color="orange" variant="light">
                                −{forade}
                              </Badge>
                            </Tooltip>
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed">
                          {data(t.criadoEm)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs" justify="flex-end" wrap="nowrap">
                          {capacidades.disparar && (
                            <>
                              <Button size="xs" variant="subtle" onClick={() => setMarcando(t)}>
                                Marcar pessoas
                              </Button>
                              <Button size="xs" variant="subtle" onClick={() => setEditando(t)}>
                                Renomear
                              </Button>
                            </>
                          )}
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}

        <BlocoDeAjuda
          secoes={[
            {
              id: 'tags',
              titulo: 'Para que servem as tags, e o que os dois números significam',
              corpo: (
                <>
                  <Text size="sm">
                    <strong>Contatos</strong> é quanta gente está marcada na tag.{' '}
                    <strong>Receberiam</strong> é quantos desses de fato chegariam a receber uma
                    mensagem: os outros estão sem celular utilizável no cadastro, ou pediram para
                    não receber.
                  </Text>
                  <Text size="sm">
                    Quando os dois números são diferentes, a correção é no{' '}
                    <strong>SAGA</strong> — a seção Pessoas lista quem está sem telefone e por quê.
                    Montar o aviso antes de corrigir manda a mensagem para menos gente do que você
                    pensa.
                  </Text>
                  <Text size="sm">
                    <strong>Renomear não desfaz as marcações.</strong> A tag é a mesma linha, e as
                    pessoas continuam nela. O nome é normalizado (minúsculas, sem acento, com
                    hífens) porque é ele que aparece no critério de um envio: “Turma 2026/1” e
                    “turma 2026-1” seriam duas tags para a mesma coisa, com metade da turma em
                    cada.
                  </Text>
                  <Text size="sm" c="dimmed">
                    O CSV precisa de cabeçalho com as colunas <Text span ff="monospace">{COLUNAS_ESPERADAS}</Text>,
                    separadas por vírgula. Dentro da coluna <Text span ff="monospace">tags</Text>,
                    várias tags vão separadas por ponto e vírgula. Quem já existe não é duplicado —
                    só recebe as tags.
                  </Text>
                </>
              ),
            },
          ]}
        />
      </Secao>

      <FormularioDeTag
        aberto={criando}
        aoFechar={() => setCriando(false)}
        aoSalvar={async (dados) => {
          await api.post('/admin/tags', dados);
          notifications.show({ message: 'Tag criada.', color: 'green' });
          await carregar();
        }}
      />

      <FormularioDeTag
        aberto={editando !== null}
        tag={editando}
        aoFechar={() => setEditando(null)}
        aoSalvar={async (dados) => {
          await api.patch(`/admin/tags/${editando?.id}`, dados);
          notifications.show({ message: 'Tag atualizada.', color: 'green' });
          await carregar();
        }}
      />

      {marcando && (
        <MarcarPessoas
          tag={marcando}
          aoFechar={() => setMarcando(null)}
          aoMudar={() => void carregar()}
        />
      )}

      <ImportarCsv
        aberto={importando}
        aoFechar={() => setImportando(false)}
        aoImportar={() => void carregar()}
      />
    </Stack>
  );
}

// ---------------------------------------------------------------------------

function FormularioDeTag({
  aberto,
  tag,
  aoFechar,
  aoSalvar,
}: {
  aberto: boolean;
  tag?: TagNaLista | null;
  aoFechar: () => void;
  aoSalvar: (dados: { nome: string; descricao?: string }) => Promise<void>;
}): JSX.Element {
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  // Recarrega os campos a cada abertura: sem isto, abrir "renomear" na
  // segunda tag mostraria os valores da primeira.
  useEffect(() => {
    if (!aberto) return;
    setNome(tag?.nome ?? '');
    setDescricao(tag?.descricao ?? '');
    setErro(null);
  }, [aberto, tag]);

  async function salvar(): Promise<void> {
    setSalvando(true);
    try {
      await aoSalvar({ nome, ...(descricao.trim() ? { descricao } : {}) });
      aoFechar();
    } catch (e) {
      setErro(explicarEnvio(e, 'falha ao salvar a tag'));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal opened={aberto} onClose={aoFechar} title={tag ? 'Renomear tag' : 'Nova tag'} centered>
      <Stack gap="sm">
        <TextInput
          label="Nome"
          description="Minúsculas, sem acento, com hífens. É o nome que aparece no critério de um envio."
          value={nome}
          onChange={(e) => setNome(e.currentTarget.value)}
          placeholder="turma-2026-1"
        />
        <TextInput
          label="Descrição"
          description="Opcional. O texto legível, para quem lê a lista de tags."
          value={descricao}
          onChange={(e) => setDescricao(e.currentTarget.value)}
          placeholder="Turma de janeiro de 2026 — teórico"
        />
        {tag && (
          <Text size="xs" c="dimmed">
            Renomear não desfaz as marcações: as {tag.contatos} pessoas continuam na tag.
          </Text>
        )}
        {erro && (
          <Alert color="red" variant="light" role="alert">
            <Text size="sm">{erro}</Text>
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button loading={salvando} disabled={!nome.trim()} onClick={() => void salvar()}>
            Salvar
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ---------------------------------------------------------------------------

/**
 * Seletor de pessoas para uma tag.
 *
 * O telefone sai SEMPRE mascarado desta lista, inclusive para admin: é uma
 * tela de seleção, não de consulta, e não há o que fazer aqui com o número
 * completo. A coluna que importa é o aviso de quem não receberia.
 */
function MarcarPessoas({
  tag,
  aoFechar,
  aoMudar,
}: {
  tag: TagNaLista;
  aoFechar: () => void;
  aoMudar: () => void;
}): JSX.Element {
  const [dados, setDados] = useState<ListaMarcaveis | null>(null);
  const [busca, setBusca] = useState('');
  const [tipo, setTipo] = useState('');
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set());
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    const p = new URLSearchParams();
    if (busca.trim()) p.set('busca', busca.trim());
    if (tipo) p.set('tipo', tipo);
    try {
      setDados(await api.get<ListaMarcaveis>(`/admin/contatos-marcaveis?${p.toString()}`));
      setErro(null);
    } catch (e) {
      setErro(explicarEnvio(e, 'falha ao carregar as pessoas'));
    }
  }, [busca, tipo]);

  useEffect(() => {
    const atraso = setTimeout(() => void carregar(), 250);
    return () => clearTimeout(atraso);
  }, [carregar]);

  function alternar(id: string): void {
    setSelecionados((atual) => {
      const novo = new Set(atual);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });
  }

  function temATag(c: ContatoMarcavel): boolean {
    return c.tags.some((t) => t.id === tag.id);
  }

  async function aplicar(acao: 'marcar' | 'desmarcar'): Promise<void> {
    setSalvando(true);
    try {
      await api.post(`/admin/tags/${tag.id}/contatos`, {
        acao,
        contatoIds: [...selecionados],
      });
      notifications.show({
        message: `${selecionados.size} ${selecionados.size === 1 ? 'pessoa' : 'pessoas'} ${
          acao === 'marcar' ? 'marcada(s)' : 'desmarcada(s)'
        }.`,
        color: 'green',
      });
      setSelecionados(new Set());
      await carregar();
      aoMudar();
    } catch (e) {
      setErro(explicarEnvio(e, 'falha ao aplicar a marcação'));
    } finally {
      setSalvando(false);
    }
  }

  const visiveis = dados?.itens ?? [];

  return (
    <Modal opened onClose={aoFechar} title={`Pessoas na tag “${tag.nome}”`} size="xl" centered>
      <Stack gap="sm">
        <BarraDeFiltros>
          <TextInput
            label="Buscar"
            placeholder="nome, apelido, curso, CANAC"
            value={busca}
            onChange={(e) => setBusca(e.currentTarget.value)}
            size="xs"
            w={240}
          />
          <Select
            label="Tipo"
            size="xs"
            w={180}
            value={tipo}
            onChange={(v) => setTipo(v ?? '')}
            data={[
              { value: '', label: 'Todos' },
              ...(dados?.tipos ?? []).map((t) => ({
                value: t.tipo,
                label: `${t.tipo} (${t.total})`,
              })),
            ]}
          />
        </BarraDeFiltros>

        {erro && (
          <Alert color="red" variant="light" role="alert">
            <Text size="sm">{erro}</Text>
          </Alert>
        )}

        {!dados && (
          <Group gap="xs">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              Carregando…
            </Text>
          </Group>
        )}

        {dados && visiveis.length === 0 && (
          <Vazio titulo="Nenhuma pessoa para este filtro.">
            Contato de fora do sistema acadêmico entra por <strong>Importar CSV</strong> ou pela
            tela de Conversas — e aparece aqui depois disso.
          </Vazio>
        )}

        {dados && visiveis.length > 0 && (
          <ScrollArea.Autosize mah="48vh">
            <Table highlightOnHover verticalSpacing={4} fz="sm" stickyHeader>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={36} />
                  <Table.Th>Nome</Table.Th>
                  <Table.Th>Tipo</Table.Th>
                  <Table.Th>Curso</Table.Th>
                  <Table.Th>Telefone</Table.Th>
                  <Table.Th>Nesta tag</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {visiveis.map((c) => (
                  <Table.Tr key={c.id}>
                    <Table.Td>
                      <Checkbox
                        checked={selecionados.has(c.id)}
                        onChange={() => alternar(c.id)}
                        aria-label={`Selecionar ${c.nome ?? 'contato'}`}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{c.nome ?? '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {c.tipo}
                        {c.origem !== 'saga' ? ` · ${c.origem}` : ''}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {codigoDoCurso(c.curso) ?? '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {/* O aviso vale mais que o número: marcar alguém sem
                          celular utilizável não faz a mensagem chegar. */}
                      {c.semTelefone ? (
                        <Badge size="xs" color="orange" variant="light">
                          sem telefone
                        </Badge>
                      ) : c.semEnvio ? (
                        <Badge size="xs" color="red" variant="light">
                          pediu para não receber
                        </Badge>
                      ) : (
                        <Text size="xs" ff="monospace" c="dimmed">
                          {c.telefoneMascarado ?? '—'}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {temATag(c) ? (
                        <Badge size="xs" variant="light">
                          sim
                        </Badge>
                      ) : (
                        <Text size="xs" c="dimmed">
                          —
                        </Text>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea.Autosize>
        )}

        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            {selecionados.size === 0
              ? 'Marque as caixas das pessoas que entram (ou saem) da tag.'
              : `${selecionados.size} selecionada(s)`}
          </Text>
          <Group gap="xs">
            <Button
              size="xs"
              variant="default"
              loading={salvando}
              disabled={selecionados.size === 0}
              onClick={() => void aplicar('desmarcar')}
            >
              Tirar da tag
            </Button>
            <Button
              size="xs"
              loading={salvando}
              disabled={selecionados.size === 0}
              onClick={() => void aplicar('marcar')}
            >
              Pôr na tag
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}

// ---------------------------------------------------------------------------

/**
 * Importação de CSV, com PRÉVIA obrigatória.
 *
 * O mesmo princípio da montagem de lista: o número aparece antes de o banco
 * mudar. Uma coluna trocada de lugar num arquivo de 300 linhas cria 300
 * contatos com o nome no telefone, e desfazer isso à mão é o resto do dia de
 * alguém.
 */
function ImportarCsv({
  aberto,
  aoFechar,
  aoImportar,
}: {
  aberto: boolean;
  aoFechar: () => void;
  aoImportar: () => void;
}): JSX.Element {
  const [csv, setCsv] = useState('');
  const [previa, setPrevia] = useState<PreviaDeImportacao | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    if (aberto) return;
    setCsv('');
    setPrevia(null);
    setErro(null);
  }, [aberto]);

  async function conferir(): Promise<void> {
    setOcupado(true);
    try {
      setPrevia(await api.post<PreviaDeImportacao>('/admin/contatos/importar/previa', { csv }));
      setErro(null);
    } catch (e) {
      setPrevia(null);
      setErro(explicarEnvio(e, 'falha ao conferir o CSV'));
    } finally {
      setOcupado(false);
    }
  }

  async function aplicar(): Promise<void> {
    setOcupado(true);
    try {
      const r = await api.post<ResultadoImportacao>('/admin/contatos/importar', { csv });
      notifications.show({
        title: 'Importação concluída',
        message: `${r.criados} novos, ${r.atualizados} já existiam, ${r.marcacoes} marcações de tag.`,
        color: 'green',
      });
      aoImportar();
      aoFechar();
    } catch (e) {
      setErro(explicarEnvio(e, 'falha ao importar'));
    } finally {
      setOcupado(false);
    }
  }

  return (
    <Modal opened={aberto} onClose={aoFechar} title="Importar contatos de CSV" size="lg" centered>
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Cole o conteúdo do arquivo. A primeira linha é o cabeçalho e precisa ter{' '}
          <Text span ff="monospace">
            {COLUNAS_ESPERADAS}
          </Text>
          , separadas por vírgula. Dentro da coluna{' '}
          <Text span ff="monospace">
            tags
          </Text>
          , várias tags vão separadas por ponto e vírgula.
        </Text>

        <Textarea
          label="CSV"
          autosize
          minRows={6}
          maxRows={14}
          value={csv}
          onChange={(e) => {
            setCsv(e.currentTarget.value);
            setPrevia(null);
          }}
          placeholder={'nome,telefone,tags\nAna Souza,18999998888,turma-2026-1;interessados'}
          styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
        />

        {erro && (
          <Alert color="red" variant="light" role="alert">
            <Text size="sm">{erro}</Text>
          </Alert>
        )}

        {previa && (
          <Paper withBorder p="sm" radius="md">
            <Stack gap="xs">
              <Group gap="lg">
                <Numerinho rotulo="novos" valor={previa.resumo.novos} />
                <Numerinho rotulo="já existiam" valor={previa.resumo.existentes} />
                <Numerinho
                  rotulo="inválidos"
                  valor={previa.resumo.invalidos}
                  alerta={previa.resumo.invalidos > 0}
                />
                <Numerinho rotulo="tags novas" valor={previa.resumo.tagsNovas} />
              </Group>

              {previa.resumo.existentes > 0 && (
                <Text size="xs" c="dimmed">
                  Quem já existe <strong>não é duplicado</strong> e o nome do cadastro{' '}
                  <strong>não é sobrescrito</strong> — essas linhas só acrescentam as tags.
                </Text>
              )}

              {previa.invalidos.length > 0 && (
                <Stack gap={2}>
                  <Text size="xs" fw={600}>
                    Linhas que não entram:
                  </Text>
                  {previa.invalidos.slice(0, 8).map((l) => (
                    <Text key={l.linha} size="xs" c="dimmed" ff="monospace">
                      linha {l.linha}: “{l.telefoneBruto || '(vazio)'}” · {l.motivo}
                    </Text>
                  ))}
                  {previa.invalidos.length > 8 && (
                    <Text size="xs" c="dimmed">
                      e mais {previa.resumo.invalidos - 8}.
                    </Text>
                  )}
                </Stack>
              )}

              {previa.tags.length > 0 && (
                <Group gap={4}>
                  {previa.tags.map((t) => (
                    <Badge
                      key={t.nome}
                      size="xs"
                      variant={t.nova ? 'filled' : 'light'}
                      color={t.nova ? 'blue' : 'gray'}
                    >
                      {t.nome}
                      {t.nova ? ' (nova)' : ''}
                    </Badge>
                  ))}
                </Group>
              )}
            </Stack>
          </Paper>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button
            variant="default"
            loading={ocupado && !previa}
            disabled={!csv.trim()}
            onClick={() => void conferir()}
          >
            Conferir
          </Button>
          {/* Importar só depois de conferir: a prévia é o passo que impede uma
              coluna trocada de virar 300 contatos errados. */}
          <Button loading={ocupado} disabled={previa === null} onClick={() => void aplicar()}>
            Importar
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function Numerinho({
  rotulo,
  valor,
  alerta = false,
}: {
  rotulo: string;
  valor: number;
  alerta?: boolean;
}): JSX.Element {
  return (
    <Stack gap={0}>
      <Text fz={22} fw={600} lh={1} ff="monospace" c={alerta && valor > 0 ? 'orange' : undefined}>
        {valor}
      </Text>
      <Text size="xs" c="dimmed" tt="uppercase" fw={500} style={{ letterSpacing: '0.04em' }}>
        {rotulo}
      </Text>
    </Stack>
  );
}
